import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { AlertTriangle, ArrowLeft, Search, Download, ChevronDown, Building2, BadgeCheck, Star, Loader2 } from "lucide-react";

const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");
const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const STATE_COLORS = {
  solo_marca: "bg-rose-100 text-rose-700 border-rose-200",
  marginal: "bg-amber-100 text-amber-700 border-amber-200",
  rojo: "bg-orange-100 text-orange-700 border-orange-200",
  amarillo: "bg-yellow-100 text-yellow-700 border-yellow-200",
  verde: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function MoleculesNoStockPage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("eur_desc");
  const [expandedGh, setExpandedGh] = useState(null);
  const [labsCache, setLabsCache] = useState({}); // gh -> { loading, labs, error }

  const toggleExpand = async (gh) => {
    if (!gh) return;
    if (expandedGh === gh) {
      setExpandedGh(null);
      return;
    }
    setExpandedGh(gh);
    if (labsCache[gh]) return; // ya cargado
    setLabsCache((p) => ({ ...p, [gh]: { loading: true } }));
    try {
      const r = await api.get("/catalog/gh-availability", { params: { gh } });
      setLabsCache((p) => ({ ...p, [gh]: { loading: false, labs: r.data?.labs || [] } }));
    } catch (e) {
      setLabsCache((p) => ({ ...p, [gh]: { loading: false, error: true } }));
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/dashboard/executive");
        if (alive) setData(r.data?.stock_gap || null);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const isStock = data?.mode === "stock";
  const rows = useMemo(() => {
    if (!data) return [];
    const list = isStock ? (data.all || data.top || []) : (data.top || []);
    const q = search.trim().toLowerCase();
    let filtered = q
      ? list.filter((r) =>
          (r.gh || "").toLowerCase().includes(q) ||
          (r.state_label || "").toLowerCase().includes(q) ||
          (r.preferred_lab || "").toLowerCase().includes(q),
        )
      : list;
    const sorted = [...filtered];
    if (sort === "eur_desc") sorted.sort((a, b) => (b.marca_euros || 0) - (a.marca_euros || 0));
    else if (sort === "eur_asc") sorted.sort((a, b) => (a.marca_euros || 0) - (b.marca_euros || 0));
    else if (sort === "units_desc") sorted.sort((a, b) => (b.marca_units || 0) - (a.marca_units || 0));
    else if (sort === "name") sorted.sort((a, b) => (a.gh || "").localeCompare(b.gh || "", "es"));
    return sorted;
  }, [data, search, sort, isStock]);

  const downloadExcel = async () => {
    try {
      const r = await api.get("/dashboard/stock-gap/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `moleculas-sin-stock-generico.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="p-12 text-slate-400">Cargando…</div>;

  if (!data || (!isStock && !(data.top || []).length)) {
    return (
      <div className="p-12">
        <button onClick={() => nav("/cuadro-mando")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4" /> Cuadro de mando
        </button>
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500">
          No hay datos disponibles. Sube tu stock para detectar G.H. sin genérico equivalente.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10" data-testid="molecules-no-stock-page">
      <button onClick={() => nav("/cuadro-mando")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-to-dashboard">
        <ArrowLeft className="h-4 w-4" /> Cuadro de mando
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 mb-2">
            <AlertTriangle className="h-3 w-3" /> Listado completo
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-950">
            {isStock ? "Moléculas sin stock de genérico" : "Moléculas en SOLO MARCA"}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-3xl">
            {isStock
              ? `De las ${fmtNum(data.molecules_dispensed)} G.H. dispensadas, ${fmtNum(data.molecules_without_stock)} (${(data.pct_without_stock || 0).toFixed(1)}%) no tienen ni una unidad del genérico equivalente en tu stock. € marca en riesgo: ${fmtEur(data.marca_euros_at_risk)} · ${fmtNum(data.marca_units_at_risk)} uds. Haz click en una molécula para ver qué laboratorios tienen el genérico.`
              : `Estas G.H. solo se dispensan en marca, sin stock de genérico cargado para confirmar disponibilidad.`}
          </p>
        </div>
        <button
          onClick={downloadExcel}
          data-testid="download-excel"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg whitespace-nowrap"
        >
          <Download className="h-4 w-4" /> Excel
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por molécula, estado o laboratorio preferente..."
            className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="search-input"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          data-testid="sort-select"
        >
          <option value="eur_desc">€ marca · mayor primero</option>
          <option value="eur_asc">€ marca · menor primero</option>
          <option value="units_desc">Unidades · mayor primero</option>
          <option value="name">Molécula · A-Z</option>
        </select>
      </div>

      <div className="text-xs text-slate-500 mb-2 font-mono-num">
        Mostrando {rows.length} de {(data.all || data.top || []).length} moléculas
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="molecules-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left p-3 font-semibold">#</th>
                <th className="text-left p-3 font-semibold">Molécula · G.H.</th>
                <th className="text-left p-3 font-semibold">Estado</th>
                <th className="text-right p-3 font-semibold">Uds marca</th>
                <th className="text-right p-3 font-semibold">€ marca</th>
                <th className="text-left p-3 font-semibold">Lab. preferente</th>
                <th className="text-right p-3 font-semibold">CN gen. en catálogo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-slate-400">
                    No hay moléculas que coincidan con la búsqueda.
                  </td>
                </tr>
              )}
              {rows.map((r, idx) => {
                const isOpen = expandedGh === r.gh;
                const cache = labsCache[r.gh];
                return (
                  <React.Fragment key={`${r.gh}-${idx}`}>
                    <tr
                      className={`hover:bg-amber-50/30 cursor-pointer ${isOpen ? "bg-amber-50/50" : ""}`}
                      onClick={() => toggleExpand(r.gh)}
                      data-testid={`molecule-row-${idx}`}
                    >
                      <td className="p-3 text-xs text-slate-400 font-mono-num">
                        <span className="inline-flex items-center gap-1.5">
                          <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          {idx + 1}
                        </span>
                      </td>
                      <td className="p-3 font-medium text-slate-900 max-w-[420px] truncate" title={r.gh}>{r.gh || "—"}</td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATE_COLORS[r.state_key] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {r.state_label || r.state_key || "—"}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono-num font-semibold text-rose-700">{fmtNum(r.marca_units)}</td>
                      <td className="p-3 text-right font-mono-num font-bold text-rose-700">{fmtEur(r.marca_euros)}</td>
                      <td className="p-3 text-xs text-slate-600">{r.preferred_lab || <span className="text-slate-400 italic">Sin asignar</span>}</td>
                      <td className="p-3 text-right font-mono-num text-slate-600">{fmtNum(r.generic_cns_in_catalog)}</td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`molecule-expand-${idx}`}>
                        <td colSpan={7} className="p-0 bg-slate-50/70">
                          <div className="px-6 py-4 border-l-4 border-amber-300">
                            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-3 flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5" /> Laboratorios con genérico EFG en catálogo
                            </div>
                            {cache?.loading && (
                              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> Consultando catálogo…
                              </div>
                            )}
                            {cache?.error && (
                              <div className="text-sm text-rose-600 py-2">No se pudo cargar la disponibilidad. Inténtalo de nuevo.</div>
                            )}
                            {!cache?.loading && !cache?.error && (cache?.labs || []).length === 0 && (
                              <div className="text-sm text-slate-500 py-2">Ningún laboratorio tiene el genérico de esta G.H. en el catálogo.</div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                              {(cache?.labs || []).map((lab) => (
                                <div
                                  key={lab.laboratorio}
                                  className={`rounded-xl border p-3 bg-white ${lab.is_preferred ? "border-amber-400 ring-1 ring-amber-200" : lab.has_agreement ? "border-emerald-300" : "border-slate-200"}`}
                                  data-testid={`lab-card-${lab.laboratorio}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="text-sm font-bold text-slate-900 leading-tight">{lab.laboratorio}</div>
                                    <div className="flex gap-1 shrink-0">
                                      {lab.is_preferred && (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                          <Star className="h-2.5 w-2.5" /> Preferente
                                        </span>
                                      )}
                                      {lab.has_agreement && (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                                          <BadgeCheck className="h-2.5 w-2.5" /> Convenio
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-[10px] text-slate-500 mt-0.5 font-mono-num">{lab.cn_count} CN genérico{lab.cn_count === 1 ? "" : "s"}</div>
                                  <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto pr-1">
                                    {(lab.cns || []).map((c) => (
                                      <li key={c.cn} className="flex items-baseline justify-between gap-2 text-[11px] text-slate-600">
                                        <span className="truncate" title={c.name}>
                                          <span className="font-mono-num text-slate-400 mr-1.5">{c.cn}</span>
                                          {c.name}
                                        </span>
                                        <span className="font-mono-num text-slate-500 whitespace-nowrap">{c.pvl != null ? fmtEur(c.pvl) : "—"}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          onClick={() => nav("/proveedores")}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg"
          data-testid="go-suppliers"
        >
          Asignar laboratorios preferentes →
        </button>
        <button
          onClick={() => nav("/pedido/falta")}
          className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg"
          data-testid="go-pedido-falta"
        >
          Ir al pedido de falta →
        </button>
      </div>
    </div>
  );
}
